/**
 * useToast Hook
 * Standardized notifications using Ant Design message/notification
 * Replaces scattered message.* calls across the app
 */

import { useCallback } from 'react';
import { message, notification } from 'antd';

// Configure global message settings
message.config({
  top: 64, // Below BrandBar
  duration: 3,
  maxCount: 3,
});

// Configure global notification settings
notification.config({
  placement: 'topRight',
  top: 72, // Below BrandBar
  duration: 4.5,
  maxCount: 3,
});

/**
 * useToast hook
 * Provides standardized toast/notification methods
 *
 * @param {Object} options - Toast configuration
 * @param {number} options.duration - Default duration in seconds
 * @param {string} options.placement - Notification placement
 * @returns {Object} Toast methods
 */
export default function useToast(options = {}) {
  const {
    duration: defaultDuration,
    placement: defaultPlacement = 'topRight',
  } = options;

  // Success message (simple toast)
  const success = useCallback((content, duration) => {
    message.success({
      content,
      duration: duration || defaultDuration,
    });
  }, [defaultDuration]);

  // Error message (simple toast)
  const error = useCallback((content, duration) => {
    message.error({
      content,
      duration: duration || defaultDuration,
    });
  }, [defaultDuration]);

  // Warning message (simple toast)
  const warning = useCallback((content, duration) => {
    message.warning({
      content,
      duration: duration || defaultDuration,
    });
  }, [defaultDuration]);

  // Info message (simple toast)
  const info = useCallback((content, duration) => {
    message.info({
      content,
      duration: duration || defaultDuration,
    });
  }, [defaultDuration]);

  // Loading message (stays until manually closed)
  const loading = useCallback((content) => {
    return message.loading({
      content,
      duration: 0, // Stay until manually closed
    });
  }, []);

  // Success notification (richer, with title + description)
  const successNotification = useCallback(({ title, description, duration, placement, ...rest }) => {
    notification.success({
      message: title,
      description,
      duration: duration || defaultDuration,
      placement: placement || defaultPlacement,
      ...rest,
    });
  }, [defaultDuration, defaultPlacement]);

  // Error notification (richer, with title + description)
  const errorNotification = useCallback(({ title, description, duration, placement, ...rest }) => {
    notification.error({
      message: title,
      description,
      duration: duration || defaultDuration || 6, // Errors stay longer
      placement: placement || defaultPlacement,
      ...rest,
    });
  }, [defaultDuration, defaultPlacement]);

  // Warning notification (richer, with title + description)
  const warningNotification = useCallback(({ title, description, duration, placement, ...rest }) => {
    notification.warning({
      message: title,
      description,
      duration: duration || defaultDuration,
      placement: placement || defaultPlacement,
      ...rest,
    });
  }, [defaultDuration, defaultPlacement]);

  // Info notification (richer, with title + description)
  const infoNotification = useCallback(({ title, description, duration, placement, ...rest }) => {
    notification.info({
      message: title,
      description,
      duration: duration || defaultDuration,
      placement: placement || defaultPlacement,
      ...rest,
    });
  }, [defaultDuration, defaultPlacement]);

  // Generic notification (custom icon/type)
  const notify = useCallback(({ title, description, type = 'info', duration, placement, ...rest }) => {
    notification[type]({
      message: title,
      description,
      duration: duration || defaultDuration,
      placement: placement || defaultPlacement,
      ...rest,
    });
  }, [defaultDuration, defaultPlacement]);

  // Destroy all messages
  const destroyAll = useCallback(() => {
    message.destroy();
    notification.destroy();
  }, []);

  // Promise-based toast (shows loading, then success/error)
  const promise = useCallback(async (promiseFn, {
    loadingMessage = 'Processing...',
    successMessage = 'Success!',
    errorMessage = 'Failed',
  } = {}) => {
    const hide = loading(loadingMessage);

    try {
      const result = await promiseFn();
      hide();
      success(successMessage);
      return result;
    } catch (err) {
      hide();
      error(errorMessage);
      throw err;
    }
  }, [loading, success, error]);

  return {
    // Simple messages (toast)
    success,
    error,
    warning,
    info,
    loading,

    // Rich notifications
    successNotification,
    errorNotification,
    warningNotification,
    infoNotification,
    notify,

    // Utilities
    destroyAll,
    promise,

    // Direct access to Ant Design APIs (for advanced use)
    message,
    notification,
  };
}

// Export standalone functions for use outside components
export const toast = {
  success: (content, duration) => message.success({ content, duration }),
  error: (content, duration) => message.error({ content, duration }),
  warning: (content, duration) => message.warning({ content, duration }),
  info: (content, duration) => message.info({ content, duration }),
  loading: (content) => message.loading({ content, duration: 0 }),
};

export const notify = {
  success: ({ title, description, ...rest }) =>
    notification.success({ message: title, description, ...rest }),
  error: ({ title, description, ...rest }) =>
    notification.error({ message: title, description, ...rest }),
  warning: ({ title, description, ...rest }) =>
    notification.warning({ message: title, description, ...rest }),
  info: ({ title, description, ...rest }) =>
    notification.info({ message: title, description, ...rest }),
};
